import React, { useEffect, useRef, useState } from 'react';

interface CareerStep {
  title: string;
  years: string;
  salary: string;
  responsibilities: string[];
  skills: string[];
}

const CareerPath: React.FC = () => {
  // Track visibility state for each element
  const [visibleSteps, setVisibleSteps] = useState<boolean[]>([false, false, false, false]);
  const [whyCloudVisible, setWhyCloudVisible] = useState(false);
  
  const careerSteps: CareerStep[] = [
    {
      title: "Junior Azure Cloud Engineer",
      years: "0-2 years",
      salary: "£35,000 - £45,000",
      responsibilities: [
        "Setting up and managing basic Azure resources",
        "Monitoring cloud infrastructure",
        "Assisting with deployments",
        "Troubleshooting common issues"
      ],
      skills: [
        "Azure fundamentals",
        "Basic networking concepts",
        "Linux command line",
        "PowerShell/Azure CLI"
      ]
    },
    {
      title: "Mid-level Azure Cloud Engineer",
      years: "2-4 years",
      salary: "£45,000 - £65,000",
      responsibilities: [
        "Designing and implementing cloud solutions",
        "Automation with Infrastructure as Code (Terraform)",
        "Setting up CI/CD pipelines",
        "Cloud security implementation"
      ],
      skills: [
        "Advanced Azure services",
        "Terraform",
        "Azure DevOps/GitHub Actions",
        "Cloud architecture patterns"
      ]
    },
    {
      title: "Senior Azure Cloud Engineer",
      years: "4-6+ years",
      salary: "£65,000 - £90,000+",
      responsibilities: [
        "Architecting complex cloud solutions",
        "Leading cloud transformation initiatives",
        "Optimization of costs and performance",
        "Mentoring junior engineers"
      ],
      skills: [
        "Multi-cloud strategies",
        "Enterprise architecture",
        "Advanced security practices",
        "Cost optimization"
      ]
    },
    {
      title: "Cloud Architect / Lead Engineer",
      years: "6+ years",
      salary: "£80,000 - £120,000+",
      responsibilities: [
        "Designing enterprise-wide cloud strategies",
        "Building multi-cloud solutions",
        "Leading large-scale migrations",
        "Defining governance and security frameworks"
      ],
      skills: [
        "Enterprise cloud governance",
        "Solution architecture",
        "Stakeholder management",
        "Strategic planning"
      ]
    }
  ];

  // Create refs array using callback ref pattern
  const careerStepRefs = useRef<(HTMLDivElement | null)[]>([]);
  const whyCloudRef = useRef<HTMLDivElement | null>(null);

  // Callback ref function to set array elements
  const setCareerStepRef = (index: number) => (element: HTMLDivElement | null) => {
    careerStepRefs.current[index] = element;
  };

  useEffect(() => {
    // Set up intersection observer
    const observerOptions = {
      root: null,
      rootMargin: '50px',
      threshold: 0.1
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          careerStepRefs.current.forEach((ref, index) => {
            if (ref === entry.target) {
              setVisibleSteps(prev => {
                const newVisible = [...prev];
                newVisible[index] = true;
                return newVisible;
              });
            }
          });
          
          if (whyCloudRef.current === entry.target) {
            setWhyCloudVisible(true);
          }
        }
      });
    }, observerOptions);

    // Observe career step elements
    careerStepRefs.current.forEach(ref => {
      if (ref) observer.observe(ref);
    });

    // Observe why cloud section
    if (whyCloudRef.current) {
      observer.observe(whyCloudRef.current);
    }

    return () => {
      observer.disconnect();
    };
  }, []);

  // Style for hidden elements
  const hiddenStyle = {
    opacity: 0,
    transform: 'translateY(30px)',
    transition: 'opacity 0.8s ease-out, transform 0.8s ease-out'
  };

  // Style for visible elements
  const visibleStyle = {
    opacity: 1,
    transform: 'translateY(0)',
    transition: 'opacity 0.8s ease-out, transform 0.8s ease-out'
  };

  return (
    <section className="py-16 bg-gradient-to-b from-[#F8FAFC] to-white">
      <div className="max-w-4xl mx-auto px-4">
        <div className="text-center mb-16">
          <h2 className="text-4xl font-bold text-[#15213A] mb-4 font-heading">
            Azure Cloud Engineering Career Path
          </h2>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto leading-relaxed">
            See how your career and earning potential can grow as you progress from Junior to 
            Senior Azure Cloud Engineer in the UK job market.
          </p>
        </div>

        {/* Career progression path */}
        <div className="relative">
          {/* Vertical line connecting the boxes */}
          <div className="absolute left-1/2 top-0 bottom-0 w-0.5 bg-gradient-to-b from-[#1C98ED]/20 to-[#1C98ED]/80 hidden md:block" />
          
          <div className="space-y-12 relative">
            {careerSteps.map((step, index) => (
              <div 
                key={index}
                ref={setCareerStepRef(index)}
                style={visibleSteps[index] ? visibleStyle : hiddenStyle}
                className={`relative ${index % 2 === 0 ? 'md:pr-[50%]' : 'md:pl-[50%] md:flex md:justify-end'}`}
              >
                {/* Connector dot */}
                <div className="hidden md:block absolute top-1/2 left-1/2 w-4 h-4 rounded-full bg-[#1C98ED] transform -translate-x-1/2 -translate-y-1/2 shadow-lg" />
                
                <div className={`bg-white rounded-xl shadow-lg hover:shadow-xl transition-shadow duration-300 p-6 relative overflow-hidden max-w-md mx-auto md:mx-0 ${index % 2 === 0 ? 'md:mr-8' : 'md:ml-8'}`}>
                  {/* Background decoration */}
                  <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-[#1C98ED]/5 to-transparent rounded-bl-full -z-0" />
                  
                  <div className="relative z-10">
                    <h3 className="text-2xl font-bold text-[#15213A] mb-4">{step.title}</h3>
                    <div className="flex flex-wrap items-center gap-3">
                      <span className="bg-[#1C98ED]/10 text-[#1C98ED] px-4 py-2 rounded-full font-medium">
                        {step.years}
                      </span>
                      <span className="bg-green-50 text-green-700 px-4 py-2 rounded-full font-medium">
                        {step.salary}
                      </span>
                    </div>
                    
                    {/* Journey stage description */}
                    <div className="mt-4 text-gray-600">
                      {index === 0 && "Starting your cloud journey"}
                      {index === 1 && "Building expertise"}
                      {index === 2 && "Leading and mentoring"}
                      {index === 3 && "Shaping cloud strategies"}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
        
        {/* Why Cloud Engineering */}
        <div 
          ref={whyCloudRef}
          style={whyCloudVisible ? visibleStyle : hiddenStyle}
          className="bg-white rounded-xl shadow-lg p-8 border border-gray-100 max-w-4xl mx-auto relative overflow-hidden mt-16"
        >
          {/* Background decoration */}
          <div className="absolute inset-0 bg-gradient-to-br from-[#1C98ED]/5 via-transparent to-transparent" />
          
          <div className="relative z-10">
            <h3 className="text-2xl font-bold text-[#15213A] mb-8 text-center">Why Cloud Engineering?</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-8">
              <div className="text-center p-6 rounded-lg bg-white shadow-sm hover:shadow-md transition-shadow duration-300">
                <div className="text-3xl mb-4">📈</div>
                <h4 className="font-semibold text-[#15213A] mb-2">High Demand</h4>
                <p className="text-gray-600">Cloud skills shortage means more job opportunities</p>
              </div>
              <div className="text-center p-6 rounded-lg bg-white shadow-sm hover:shadow-md transition-shadow duration-300">
                <div className="text-3xl mb-4">💷</div>
                <h4 className="font-semibold text-[#15213A] mb-2">Excellent Salary</h4>
                <p className="text-gray-600">Above-average compensation compared to other IT roles</p>
              </div>
              <div className="text-center p-6 rounded-lg bg-white shadow-sm hover:shadow-md transition-shadow duration-300">
                <div className="text-3xl mb-4">🚀</div>
                <h4 className="font-semibold text-[#15213A] mb-2">Fast Growth</h4>
                <p className="text-gray-600">Rapid career progression with the right skills</p>
              </div>
            </div>

            {/* Additional benefits */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div className="p-6 rounded-lg bg-white shadow-sm hover:shadow-md transition-shadow duration-300">
                <div className="flex items-center mb-4">
                  <span className="text-3xl mr-4">🏠</span>
                  <div>
                    <h4 className="font-semibold text-[#15213A] mb-1">Flexible Working</h4>
                    <p className="text-gray-600">Remote and hybrid opportunities widely available across the industry</p>
                  </div>
                </div>
              </div>

              <div className="p-6 rounded-lg bg-white shadow-sm hover:shadow-md transition-shadow duration-300">
                <div className="flex items-center mb-4">
                  <span className="text-3xl mr-4">🌍</span>
                  <div>
                    <h4 className="font-semibold text-[#15213A] mb-1">Work Anywhere</h4>
                    <p className="text-gray-600">Cloud skills are in demand globally, opening international opportunities</p>
                  </div>
                </div>
              </div>

              <div className="p-6 rounded-lg bg-white shadow-sm hover:shadow-md transition-shadow duration-300">
                <div className="flex items-center mb-4">
                  <span className="text-3xl mr-4">💼</span>
                  <div>
                    <h4 className="font-semibold text-[#15213A] mb-1">Contracting Potential</h4>
                    <p className="text-gray-600">Experienced cloud engineers can earn £500-£800+ daily rates as contractors</p>
                  </div>
                </div>
              </div>

              <div className="p-6 rounded-lg bg-white shadow-sm hover:shadow-md transition-shadow duration-300">
                <div className="flex items-center mb-4">
                  <span className="text-3xl mr-4">🎯</span>
                  <div>
                    <h4 className="font-semibold text-[#15213A] mb-1">Project Variety</h4>
                    <p className="text-gray-600">Work on diverse projects across different industries and technologies</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="text-center mt-8">
          <p className="text-gray-500 text-sm italic">
            *Salary data based on UK market averages as of 2024. Individual compensation may vary based on location, company size, and specific skills.
          </p>
        </div>
      </div>
    </section>
  );
};

export default CareerPath; 